import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import API from '../../services/api';
import useEntityAlerts from '../../hooks/useEntityAlerts';
import './AdminRowAlerts.css';

const statusLabel = { active: 'Actif', suspended: 'Suspendu' };
const statusClass = { active: 'badge-active', suspended: 'badge-suspended' };

const ClientsAdmin = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const [clients, setClients]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const { alertMap, markSeen, markTreated } = useEntityAlerts('client');
  const rowRefs         = useRef({});
  const lastHighlightId = useRef(null);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (search) params.search = search;
      const res = await API.get('/admin/clients', { params });
      setClients(res.data.clients || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterStatus, search]);

  useEffect(() => {
    const t = setTimeout(fetchClients, 300);
    return () => clearTimeout(t);
  }, [fetchClients]);

  // Scroll to + highlight the row when navigating from a notification
  useEffect(() => {
    if (loading) return;
    const highlight = location.state?.highlightEntity;
    if (!highlight || highlight.type !== 'client') return;
    if (lastHighlightId.current === highlight.id) return;
    const el = rowRefs.current[highlight.id];
    if (el) {
      lastHighlightId.current = highlight.id;
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('row-alert-highlight');
        markSeen(highlight.id);
        setTimeout(() => el.classList.remove('row-alert-highlight'), 1950);
      }, 100);
    }
  }, [loading, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSuspend = async (id) => {
    try { await API.put(`/admin/clients/${id}/suspend`); markTreated(id); await fetchClients(); } catch (e) { console.error(e); }
  };

  const handleReactivate = async (id) => {
    try { await API.put(`/admin/clients/${id}/reactivate`); markTreated(id); await fetchClients(); } catch (e) { console.error(e); }
  };

  const totals = {
    count: clients.length,
    active: clients.filter(c => c.status === 'active').length,
    points: clients.reduce((a, c) => a + parseFloat(c.total_points || 0), 0),
    other: clients.filter(c => c.status !== 'active').length,
  };

  return (
    <div>
      {/* Stats */}
      <div className="admin-kpi-grid">
        {[
          { icon: 'fa-users', label: 'Total clients', value: totals.count, color: '#3b82f6' },
          { icon: 'fa-user-check', label: 'Actifs', value: totals.active, color: '#10b981' },
          { icon: 'fa-coins', label: 'Points distribués', value: totals.points.toLocaleString('fr'), color: '#f59e0b' },
          { icon: 'fa-user-slash', label: 'Suspendus', value: totals.other, color: '#ef4444' },
        ].map((k, i) => (
          <div key={i} className="admin-kpi-card">
            <div className="admin-kpi-header">
              <div className="admin-kpi-icon" style={{ background: `${k.color}18`, color: k.color }}>
                <i className={`fas ${k.icon}`}></i>
              </div>
            </div>
            <div className="admin-kpi-value">{k.value}</div>
            <div className="admin-kpi-label">{k.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="admin-filters-bar">
        <input
          className="admin-filter-input"
          placeholder="Rechercher par nom, email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className="admin-filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Tous les statuts</option>
          <option value="active">Actif</option>
          <option value="suspended">Suspendu</option>
        </select>
        <button className="admin-btn-secondary" onClick={fetchClients}>
          <i className="fas fa-sync-alt"></i> Actualiser
        </button>
      </div>

      {/* Table */}
      <div className="admin-table-card">
        {loading ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: 20, color: '#3b82f6', display: 'block', marginBottom: 8 }}></i>
            Chargement...
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Email</th>
                <th>Partenaire</th>
                <th>Points</th>
                <th>Transactions</th>
                <th>Dépensé</th>
                <th>Inscription</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map(c => {
                const clientLabel = `${c.first_name} ${c.last_name}`;
                const alertStatus = alertMap[String(c.id)];
                const alertClass  = alertStatus === 'new'  ? 'row-alert-new'
                                  : alertStatus === 'seen' ? 'row-alert-seen'
                                  : '';
                return (
                <tr
                  key={c.id}
                  ref={el => { if (el) rowRefs.current[c.id] = el; }}
                  className={alertClass}
                  onClick={() => {
                    if (alertStatus === 'new') markSeen(c.id);
                    navigate('/dashboard/admin/transactions', {
                      state: { search: c.first_name, tab: 'b2c', from: 'client', label: clientLabel }
                    });
                  }}
                  style={{ cursor: 'pointer' }}
                  title={`Voir les transactions de ${clientLabel}`}
                >
                  <td>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <strong>{clientLabel}</strong>
                      {alertStatus === 'new' && <span className="badge-new-alert">Nouveau</span>}
                      <i className="fas fa-history" style={{ fontSize: 10, color: '#94a3b8' }}></i>
                    </span>
                  </td>
                  <td style={{ color: '#64748b' }}>{c.email}</td>
                  <td style={{ color: '#64748b' }}>{c.partner_name || '-'}</td>
                  <td>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <i className="fas fa-coins" style={{ color: '#f59e0b', fontSize: 11 }}></i>
                      {(c.total_points || 0).toLocaleString('fr')}
                    </span>
                  </td>
                  <td>{c.transaction_count || 0}</td>
                  <td>{c.total_spent ? `${parseFloat(c.total_spent).toFixed(2)} TND` : '0.00 TND'}</td>
                  <td style={{ fontSize: 12, color: '#94a3b8' }}>{new Date(c.created_at).toLocaleDateString('fr')}</td>
                  <td><span className={statusClass[c.status] || 'badge-inactive'}>{statusLabel[c.status] || c.status}</span></td>
                  <td onClick={e => e.stopPropagation()}>
                    <button
                      className="admin-btn-icon"
                      title="Voir les transactions"
                      onClick={() => navigate('/dashboard/admin/transactions', {
                        state: { search: c.first_name, tab: 'b2c', from: 'client', label: clientLabel }
                      })}
                    >
                      <i className="fas fa-history"></i>
                    </button>
                    {c.status === 'active' && (
                      <button className="admin-btn-icon suspend" title="Suspendre" onClick={() => handleSuspend(c.id)}>
                        <i className="fas fa-pause"></i>
                      </button>
                    )}
                    {c.status === 'suspended' && (
                      <button className="admin-btn-icon approve" title="Réactiver" onClick={() => handleReactivate(c.id)}>
                        <i className="fas fa-play"></i>
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
              {clients.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', color: '#94a3b8', padding: '30px 0' }}>Aucun client trouvé</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ClientsAdmin;
